import { useEffect, useState, type ReactNode } from 'react';
import {
  Gauge,
  Shield,
  List as ListIcon,
  Image as ImageIcon,
  Tag,
  Link as LinkIcon,
  Share2,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  RAIL_FILTER_EVENT,
  type RailFilterId,
} from './rail/railFilter';

export interface MiniRailItem {
  id: string;
  label: string;
  icon: ReactNode;
  /** Optional small numeric/text badge in the top-right corner of the cell. */
  value?: string | number;
}

// eslint-disable-next-line react-refresh/only-export-components
export const DEFAULT_MINI_RAIL_ITEMS: MiniRailItem[] = [
  { id: 'score', label: 'Score', icon: <Gauge className="size-4" /> },
  { id: 'quality', label: 'Quality', icon: <Shield className="size-4" /> },
  { id: 'structure', label: 'Outline', icon: <ListIcon className="size-4" /> },
  { id: 'images', label: 'Images', icon: <ImageIcon className="size-4" /> },
  { id: 'meta', label: 'Meta', icon: <Tag className="size-4" /> },
  { id: 'links', label: 'Links', icon: <LinkIcon className="size-4" /> },
  { id: 'social', label: 'Social', icon: <Share2 className="size-4" /> },
];

export interface SideMiniRailProps {
  items?: MiniRailItem[];
  /**
   * Called when the user clicks a rail icon. Default behavior (when this is
   * omitted) asks EditorRail to open Tools and FILTER the card stack to that
   * section group; clicking the active cell again clears the filter.
   */
  onJumpTo?: (id: string) => void;
  className?: string;
}

const MINI_RAIL_JUMP_EVENT = 'ed-mini-rail-jump';

const defaultJump = (id: string) => {
  if (typeof window === 'undefined') return;
  // detail.itemId drives the section FILTER in EditorRail (toggle semantics).
  // detail.sectionId is intentionally omitted — that shape is reserved for
  // card-id jumps (issue-badge field cursor), which filter + scroll instead.
  window.dispatchEvent(
    new CustomEvent(MINI_RAIL_JUMP_EVENT, {
      detail: { itemId: id },
    }),
  );
};

/**
 * Vertical icon nav anchored between the doc surface and the right rail.
 * Each cell filters the Tools stack to its section group via EditorRail's
 * mini-rail event (or runs an explicit `onJumpTo` handler). The active cell is
 * highlighted by listening to EditorRail's filter-change announcements.
 */
export function SideMiniRail({ items = DEFAULT_MINI_RAIL_ITEMS, onJumpTo, className }: SideMiniRailProps) {
  const [activeFilter, setActiveFilter] = useState<RailFilterId | null>(null);

  useEffect(() => {
    const onFilterChange = (event: Event) => {
      const filter = (event as CustomEvent<{ filter?: RailFilterId | null }>).detail
        ?.filter;
      setActiveFilter(filter ?? null);
    };
    window.addEventListener(RAIL_FILTER_EVENT, onFilterChange);
    return () => window.removeEventListener(RAIL_FILTER_EVENT, onFilterChange);
  }, []);

  return (
    <div
      className={cn(
        'w-12 shrink-0 flex flex-col items-center gap-0.5 py-3 bg-card border-l border-r border-border',
        className,
      )}
    >
      {items.map((item) => {
        const isActive = activeFilter === item.id;
        return (
          <button
            key={item.id}
            type="button"
            title={isActive ? `${item.label} — click to show all` : item.label}
            aria-label={item.label}
            aria-pressed={isActive}
            onClick={() => (onJumpTo ?? defaultJump)(item.id)}
            className={cn(
              'relative flex flex-col items-center gap-0.5 w-9 px-1 py-1.5 rounded-md border-none cursor-pointer transition-colors',
              isActive
                ? 'bg-coral-light/60 text-coral-dark'
                : 'text-muted-foreground bg-transparent hover:bg-warm-bg hover:text-foreground',
            )}
          >
            <span className="inline-flex">{item.icon}</span>
            <span
              className={cn(
                'text-[8.5px] font-bold uppercase tracking-wider',
                isActive ? 'text-coral-dark' : 'text-muted-foreground',
              )}
            >
              {item.label}
            </span>
            {item.value !== undefined && (
              <span className="absolute top-0.5 right-0.5 font-mono text-[8px] font-bold text-teal-dark">
                {item.value}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
